import React from 'react';
import {
    SingleSelectionAction,
    FileUploadAction,
    MultiSelectionAction,
    BooleanAction,
    TextAction,
} from '../components/ToolsComponents/AnswerActionComponents';
import {
    UnifiedAnswerType,
    IFileUploadActionOwnProps,
    IMultiSelectionActionOwnProps,
    IBooleanActionOwnProps,
    ITextActionOwnProps,
    AllAnswerOwnProps,
} from '../components/ToolsComponents/AnswerActionComponents/AnswerActionComponents.interfaces';
import { CompanyQuestionType, ICompanyAnsweredQuestion } from '../common';
import { InputPlaceholderStyled } from '../components/ToolsComponents/ActiveChat/ChatInput/ChatInput.styles';
import { useTranslation } from 'react-i18next';

// Define a type for the question parameter that can be a full ICompanyQuestion or just its type
export type AnswerComponentConfigQuestion =
    Partial<ICompanyAnsweredQuestion> & {
        type: CompanyQuestionType;
    };

interface AnswerComponentConfigReturn {
    Component: React.ElementType;
    props: AllAnswerOwnProps | Record<string, never>; // Use Record<string, never> for empty props case
    initialAnswer?: UnifiedAnswerType;
}

export const useAnswerComponentConfig = (
    question: AnswerComponentConfigQuestion | null // Allow null question
): AnswerComponentConfigReturn | null => {
    const { t } = useTranslation();

    // Return null if question is null
    if (!question) {
        return null;
    }

    switch (question.type) {
        case CompanyQuestionType.SINGLE_SELECTION:
            return {
                Component: SingleSelectionAction,
                props: {
                    options: question.options || [], // options might be undefined
                },
                initialAnswer:
                    question.answerOptions && question.answerOptions.length > 0
                        ? question.answerOptions[0]
                        : undefined,
            };
        case CompanyQuestionType.FILE_UPLOAD:
            return {
                Component: FileUploadAction,
                props: {} as IFileUploadActionOwnProps,
                initialAnswer: undefined,
            };
        case CompanyQuestionType.MULTI_SELECTION:
            return {
                Component: MultiSelectionAction,
                props: {
                    options: question.options,
                } as IMultiSelectionActionOwnProps,
                initialAnswer: [],
            };
        case CompanyQuestionType.BOOLEAN:
            return {
                Component: BooleanAction,
                props: {} as IBooleanActionOwnProps,
                initialAnswer: undefined,
            };
        case CompanyQuestionType.SHORT_TEXT:
        case CompanyQuestionType.LONG_TEXT:
            return {
                Component: TextAction,
                props: {
                    isLongText: question.type === CompanyQuestionType.LONG_TEXT,
                    maxLength:
                        question.type === CompanyQuestionType.SHORT_TEXT
                            ? 100
                            : undefined,
                    placeholder:
                        question.type === CompanyQuestionType.LONG_TEXT
                            ? t(
                                  'tools.companyDetails.enterLongAnswerPlaceholder'
                              )
                            : t(
                                  'tools.companyDetails.enterShortAnsweerPlaceholder'
                              ),
                } as ITextActionOwnProps,
                initialAnswer: '',
            };
        case CompanyQuestionType.FREE_TEXT:
            return {
                Component: InputPlaceholderStyled,
                props: {},
                initialAnswer: '',
            };
        default: {
            return {
                Component: InputPlaceholderStyled,
                props: {},
                initialAnswer: '',
            };
        }
    }
};
